import {
  Headphones,
  Flag,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Calendar,
  MessageSquare,
  Eye,
  ChevronDown,
  ChevronUp,
  Shield,
  Ban,
  AlertOctagon,
  Edit3,
  Send,
  RefreshCw,
  MapPin,
  Users,
  Trash2,
  Bell,
  Loader2,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "../ui/use-toast";
import {
  getMyReports,
  getMyReportsStats,
  getReportsOnMyContent,
  getReportsOnMyContentStats,
  Report as ApiReport,
  ReportsStats,
  OwnContentReport,
  resubmitEvent,
  ResubmitEventPayload,
  cancelFlaggedEvent,
} from "@/api/community";
import { usePosthog } from "@/hooks/usePosthog";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "../ui/calendar";
import Tiptap, { TiptapRef } from "@/components/Tiptap";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Progress } from "../ui/progress";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { TimeInput } from "../ui/time-input";
import ShadCNDatePicker from "../ui/datePicker";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

// Types for reports
export type ReportStatus = "pending" | "actioned";
export type ReportType = "post" | "comment" | "reply" | "event";
export type AdminAction = "keep" | "remove_warning" | "suspend" | "ban" | "hold";

export interface EventDetails {
  title: string;
  date: Date;
  endDate?: Date;
  location: string;
  maxParticipants?: number;
  description?: string;
  participants?: Array<{
    userId: string;
    userName: string;
  }>;
  participantCount?: number; // Total registered count from API
  status?: "active" | "cancelled";
}

export interface ReportActionDetails {
  type: AdminAction;
  takenAt: Date;
  adminNote?: string;
  // For event "remove and warn" - special fields
  editDeadline?: Date; // 24 hours from action time
  isEventHidden?: boolean;
  resubmittedAt?: Date;
  resubmitStatus?: "pending_review" | "approved" | "rejected";
  resubmitReviewNote?: string;
}

// Event metadata status for filtering sections
export type EventMetadataStatus =
  | "waiting_action"
  | "resubmitted"
  | "approved"
  | "rejected"
  | "event_cancelled";

export interface EventMetadata {
  status: EventMetadataStatus;
  resubmission_count?: number;
  remaining_attempts?: number;
  max_attempts?: number;
  has_new_action?: boolean;
}

export interface Report {
  id: string;
  type: ReportType;
  status: ReportStatus;
  reason: string;
  description?: string;
  reportedAt: Date;
  // Content info
  contentId: string;
  contentTitle?: string;
  contentPreview?: string;
  // Event details for event reports
  eventDetails?: EventDetails;
  // Event metadata for section filtering
  eventMetadata?: EventMetadata;
  // Who reported / was reported
  reportedBy?: {
    userId: string;
    name: string;
  };
  reportedUser?: {
    userId: string;
    name: string;
  };
  // Is this the user's own content that was reported?
  isOwnContent: boolean;
  // Admin action details
  action?: ReportActionDetails;
  action_taken?: AdminAction;
  admin_comment?: string;
  reviewedAt?: Date;
  reviewed_at?: Date;
}

// Helper function to transform API report to component Report format
const transformApiReport = (apiReport: ApiReport): Report => {
  // Map API status to component status
  const statusMap: Record<string, ReportStatus> = {
    pending: "pending",
    resolved: "actioned",
    actioned: "actioned",
    dismissed: "actioned",
  };

  // Map API target_type/content_type to component type
  const typeMap: Record<string, ReportType> = {
    post: "post",
    comment: "comment",
    reply: "reply",
    event: "event",
  };

  // Map action to admin action type
  const actionMap: Record<string, AdminAction> = {
    keep: "keep",
    remove_warning: "remove_warning",
    suspend: "suspend",
    ban: "ban",
    hold: "hold",
  };

  return {
    id: apiReport.id,
    type: typeMap[apiReport.content_type || apiReport.target_type] || "post",
    status: statusMap[apiReport.status] || "pending",
    reason: apiReport.reason,
    description: apiReport.description,
    reportedAt: new Date(apiReport.created_at),
    contentId: apiReport.target_id,
    contentTitle: apiReport.target?.title,
    contentPreview: apiReport.target?.content
      ? apiReport.target.content
          .replace(/<[^>]*>?/g, "")
          .trim()
          .substring(0, 100)
      : undefined,
    reportedUser: apiReport.target?.author
      ? {
          userId: apiReport.target.author.id,
          name: apiReport.target.author.full_name,
        }
      : undefined,
    isOwnContent: false,
    action: apiReport.action_taken
      ? {
          type: actionMap[apiReport.action_taken] || "hold",
          takenAt: apiReport.reviewed_at
            ? new Date(apiReport.reviewed_at)
            : new Date(apiReport.updated_at),
          adminNote: apiReport.admin_comment,
        }
      : undefined,
    action_taken: apiReport.action_taken ? actionMap[apiReport.action_taken] || "hold" : undefined,
    admin_comment: apiReport.admin_comment,
    reviewedAt: apiReport.reviewed_at ? new Date(apiReport.reviewed_at) : undefined,
    reviewed_at: apiReport.reviewed_at ? new Date(apiReport.reviewed_at) : undefined,
  };
};

const getStatusBadge = (status: ReportStatus, report?: Report, t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);

  // For "Reports on My Content", use eventMetadata.status for more specific badges
  if (report?.isOwnContent && report?.eventMetadata?.status) {
    // If event is cancelled, always show cancelled badge regardless of eventMetadata.status
    if (report.eventDetails?.status === "cancelled") {
      return (
        <Badge
          variant="outline"
          className="border-border bg-muted inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold text-slate-600"
        >
          <XCircle className="h-3 w-3" />
          {translate("community.support.status.event_cancelled")}
        </Badge>
      );
    }

    switch (report.eventMetadata.status) {
      case "waiting_action":
        return (
          <Badge
            variant="outline"
            className="inline-flex animate-pulse items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700"
          >
            <Edit3 className="h-3 w-3" />
            {translate("community.support.status.awaiting_edit")}
          </Badge>
        );
      case "resubmitted":
        return (
          <Badge
            variant="outline"
            className="border-brand-accent-border bg-brand-accent text-brand-accent-foreground inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold"
          >
            <RefreshCw className="h-3 w-3" />
            {translate("community.support.status.resubmitted_review")}
          </Badge>
        );
      case "approved":
        return (
          <Badge
            variant="outline"
            className="inline-flex items-center gap-1 rounded-full border border-[#a7f3d0] bg-[#ecfdf5] px-2.5 py-0.5 text-xs font-semibold text-[#047857]"
          >
            <CheckCircle className="h-3 w-3" />
            {translate("community.support.status.resubmit_approved")}
          </Badge>
        );
      case "rejected":
        return (
          <Badge
            variant="outline"
            className="border-destructive/30 bg-destructive/10 text-destructive inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold"
          >
            <XCircle className="h-3 w-3" />
            {translate("community.support.status.resubmit_rejected")}
          </Badge>
        );
      case "event_cancelled":
        return (
          <Badge
            variant="outline"
            className="border-border bg-muted inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold text-slate-600"
          >
            <XCircle className="h-3 w-3" />
            {translate("community.support.status.event_cancelled")}
          </Badge>
        );
    }
  }

  // Default badges for "My Reports" section
  switch (status) {
    case "pending":
      return (
        <Badge
          variant="outline"
          className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700"
        >
          <Clock className="h-3 w-3" />
          {translate("community.support.status.pending")}
        </Badge>
      );
    case "actioned":
      return (
        <Badge
          variant="outline"
          className="inline-flex items-center gap-1 rounded-full border border-[#a7f3d0] bg-[#ecfdf5] px-2.5 py-0.5 text-xs font-semibold text-[#047857]"
        >
          <CheckCircle className="h-3 w-3" />
          {translate("community.support.status.actioned")}
        </Badge>
      );
  }
};

const getTypeIcon = (type: ReportType) => {
  switch (type) {
    case "post":
      return <FileText className="h-4 w-4" />;
    case "comment":
      return <MessageSquare className="h-4 w-4" />;
    case "reply":
      return <MessageSquare className="h-4 w-4" />;
    case "event":
      return <Calendar className="h-4 w-4" />;
  }
};

const getTypeLabel = (type: ReportType, t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  switch (type) {
    case "post":
      return translate("community.support.type.post");
    case "comment":
      return translate("community.support.type.comment");
    case "reply":
      return translate("community.support.type.reply");
    case "event":
      return translate("community.support.type.event");
  }
};

const getActionBadge = (action: AdminAction | undefined, t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  switch (action) {
    case "keep":
      return (
        <Badge className="inline-flex items-center gap-1 rounded-full border border-[#a7f3d0] bg-[#ecfdf5] px-2.5 py-0.5 text-xs font-semibold text-[#047857] hover:bg-[#ecfdf5]">
          <CheckCircle className="h-3 w-3" />
          {translate("community.support.action.keep")}
        </Badge>
      );
    case "remove_warning":
      return (
        <Badge className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 hover:bg-amber-50">
          <AlertTriangle className="h-3 w-3" />
          {translate("community.support.action.remove_warning")}
        </Badge>
      );
    case "hold":
      return (
        <Badge className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 hover:bg-amber-50">
          <Clock className="h-3 w-3" />
          {translate("community.support.action.hold")}
        </Badge>
      );
    case "suspend":
      return (
        <Badge className="border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/10 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold">
          <AlertOctagon className="h-3 w-3" />
          {translate("community.support.action.suspend")}
        </Badge>
      );
    case "ban":
      return (
        <Badge className="border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/10 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold">
          <Ban className="h-3 w-3" />
          {translate("community.support.action.ban")}
        </Badge>
      );
    default:
      return null;
  }
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

// Format reason codes into proper display names
const formatReasonLabel = (reason: string, t?: (key: string) => string): string => {
  const translate = t || ((key: string) => key);

  // Map of reason codes to translation keys
  const reasonMap: Record<string, string> = {
    hate_speech: "community.support.reasons.hate_speech",
    harassment: "community.support.reasons.harassment",
    spam: "community.support.reasons.spam",
    inappropriate: "community.support.reasons.inappropriate",
    misleading: "community.support.reasons.misleading",
    violence: "community.support.reasons.violence",
    other: "community.support.reasons.other",
  };

  // Check if we have a translation key for this reason
  const translationKey = reasonMap[reason.toLowerCase()];
  if (translationKey) {
    const translated = translate(translationKey);
    // If translation returns the key itself, fall back to formatting
    if (translated !== translationKey) {
      return translated;
    }
  }

  // Fallback: Convert snake_case/kebab-case to Title Case
  return reason.replace(/[_-]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const getTimeRemaining = (deadline: Date) => {
  const now = new Date();
  const diff = deadline.getTime() - now.getTime();

  if (diff <= 0) return { expired: true, text: "Deadline passed", percentage: 0 };

  const dayMs = 24 * 60 * 60 * 1000;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const days = Math.ceil(diff / dayMs);

  // Calculate percentage (24 hours = 100%)
  const totalDuration = 24 * 60 * 60 * 1000;
  const percentage = Math.min(100, (diff / totalDuration) * 100);

  return {
    expired: false,
    text:
      diff >= dayMs
        ? `${days} ${days === 1 ? "day" : "days"} remaining`
        : `${hours}h ${minutes}m remaining`,
    percentage,
  };
};

interface ReportCardProps {
  report: Report;
  onResubmitEvent?: (reportId: string, eventDetails: EventDetails) => Promise<void>;
  onCancelEvent?: (reportId: string, reason: string) => Promise<void>;
  isHighlighted?: boolean;
}

function ReportCard({ report, onResubmitEvent, onCancelEvent, isHighlighted }: ReportCardProps) {
  const { toast } = useToast();
  const { trackEvent } = usePosthog();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // Event editing state
  const [editEventTitle, setEditEventTitle] = useState(report.eventDetails?.title || "");
  const [editEventDate, setEditEventDate] = useState<Date | undefined>(report.eventDetails?.date);
  const [editEventTime, setEditEventTime] = useState(
    report.eventDetails?.date
      ? `${String(report.eventDetails.date.getHours()).padStart(2, "0")}:${String(report.eventDetails.date.getMinutes()).padStart(2, "0")}`
      : "",
  );
  const [editEventEndTime, setEditEventEndTime] = useState(
    report.eventDetails?.endDate
      ? `${String(report.eventDetails.endDate.getHours()).padStart(2, "0")}:${String(report.eventDetails.endDate.getMinutes()).padStart(2, "0")}`
      : "",
  );
  const [editEventLocation, setEditEventLocation] = useState(report.eventDetails?.location || "");
  const [editEventMaxParticipants, setEditEventMaxParticipants] = useState(
    report.eventDetails?.maxParticipants?.toString() || "",
  );
  const [editEventDescription, setEditEventDescription] = useState(
    report.eventDetails?.description || "",
  );
  const [editEventTimeError, setEditEventTimeError] = useState<string | null>(null);
  const [editEventTitleError, setEditEventTitleError] = useState<string | null>(null);
  const editTextareaRef = useRef<TiptapRef>(null);

  // Title validation handler - no special characters allowed
  const handleEditEventTitleChange = (value: string) => {
    // Allow alphanumeric, spaces, and basic punctuation (hyphen, apostrophe, period, comma)
    const specialCharRegex = /[^a-zA-Z0-9\s\-'.,]/;
    if (specialCharRegex.test(value)) {
      setEditEventTitleError(t("community.support.event_edit.title_special_char_error"));
    } else {
      setEditEventTitleError(null);
    }
    setEditEventTitle(value);
  };

  // Time validation handler
  const handleEditEventTimeChange = (value: string) => {
    setEditEventTime(value);
    // Clear error when start time changes
    if (editEventTimeError) {
      setEditEventTimeError(null);
    }
  };

  const handleEditEventEndTimeChange = (value: string) => {
    setEditEventEndTime(value);
    // Validate end time is after start time
    if (value && editEventTime) {
      const [startH, startM] = editEventTime.split(":").map(Number);
      const [endH, endM] = value.split(":").map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      if (endMinutes <= startMinutes) {
        setEditEventTimeError(t("community.support.event_edit.end_time_error"));
      } else {
        setEditEventTimeError(null);
      }
    } else {
      setEditEventTimeError(null);
    }
  };

  const showEditSection =
    report.isOwnContent &&
    report.type === "event" &&
    report.status === "actioned" &&
    (report.action?.type === "remove_warning" || report.action?.type === "hold");

  const timeRemaining = report.action?.editDeadline
    ? getTimeRemaining(report.action.editDeadline)
    : null;
  const isActionRequiredStatus =
    report.eventMetadata?.status === "waiting_action" ||
    report.eventMetadata?.status === "rejected";
  const remainingAttempts = Math.max(
    0,
    report.eventMetadata?.remaining_attempts ??
      (report.eventMetadata?.max_attempts ?? 5) - (report.eventMetadata?.resubmission_count ?? 0),
  );

  const handleResubmit = async () => {
    // Check for special characters in title
    const specialCharRegex = /[^a-zA-Z0-9\s\-'.,]/;
    if (specialCharRegex.test(editEventTitle)) {
      setEditEventTitleError(t("community.support.event_edit.title_special_char_error"));
      toast({
        title: t("community.support.toast.validation_error_title"),
        description: t("community.support.event_edit.title_special_char_error"),
        variant: "destructive",
      });
      return;
    }

    if (!editEventTitle.trim() || !editEventDate || !editEventTime || !editEventLocation.trim()) {
      trackEvent("support_event_resubmit_validation_failed", {
        report_id: report.id,
        report_type: report.type,
        missing_fields: {
          title: !editEventTitle.trim(),
          date: !editEventDate,
          time: !editEventTime,
          location: !editEventLocation.trim(),
        },
      });
      toast({
        title: t("community.support.toast.missing_fields_title"),
        description: t("community.support.toast.missing_fields_description"),
        variant: "destructive",
      });
      return;
    }

    const [hours, minutes] = editEventTime.split(":");
    const eventDateTime = new Date(editEventDate);
    eventDateTime.setHours(parseInt(hours), parseInt(minutes));

    let eventEndDateTime: Date | undefined;
    if (editEventEndTime) {
      const [endHours, endMinutes] = editEventEndTime.split(":");
      eventEndDateTime = new Date(editEventDate);
      eventEndDateTime.setHours(parseInt(endHours), parseInt(endMinutes));
    }

    const updatedEventDetails: EventDetails = {
      title: editEventTitle,
      date: eventDateTime,
      endDate: eventEndDateTime,
      location: editEventLocation,
      maxParticipants: editEventMaxParticipants ? parseInt(editEventMaxParticipants) : undefined,
      description: editEventDescription || undefined,
      participants: report.eventDetails?.participants,
    };

    if (onResubmitEvent) {
      setIsSubmitting(true);
      try {
        trackEvent("support_event_resubmitted", {
          report_id: report.id,
          report_type: report.type,
          event_title: editEventTitle,
          has_end_time: !!editEventEndTime,
          has_max_participants: !!editEventMaxParticipants,
          has_description: !!editEventDescription,
          participants_count: report.eventDetails?.participants?.length || 0,
        });
        await onResubmitEvent(report.id, updatedEventDetails);
        setIsEditing(false);
        toast({
          title: t("community.support.toast.event_resubmitted_title"),
          description: t("community.support.toast.event_resubmitted_description"),
        });
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          t("community.support.toast.event_resubmit_failed_description");
        toast({
          title: t("community.support.toast.event_resubmit_failed_title"),
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancelEvent = async () => {
    if (onCancelEvent) {
      // Use default reason if none provided
      const reason = cancelReason.trim() || t("community.support.cancel_dialog.default_reason");

      setIsCancelling(true);
      try {
        trackEvent("support_event_cancelled", {
          report_id: report.id,
          report_type: report.type,
          event_title: report.eventDetails?.title,
          participants_count: report.eventDetails?.participants?.length || 0,
          report_status: report.status,
          report_reason: report.reason,
          cancel_reason: reason,
        });
        await onCancelEvent(report.id, reason);
        setShowCancelDialog(false);
        setCancelReason("");
        toast({
          title: t("community.support.toast.event_cancelled_title"),
          description: t("community.support.toast.event_cancelled_description"),
        });
      } catch (error) {
        toast({
          title: t("community.support.toast.event_cancel_failed_title"),
          description: t("community.support.toast.event_cancel_failed_description"),
          variant: "destructive",
        });
      } finally {
        setIsCancelling(false);
      }
    }
  };

  // Track report card expand/collapse
  const handleToggleExpand = () => {
    const newExpanded = !expanded;
    trackEvent("support_report_details_toggled", {
      report_id: report.id,
      report_type: report.type,
      report_status: report.status,
      is_own_content: report.isOwnContent,
      expanded: newExpanded,
    });
    setExpanded(newExpanded);
  };

  // Track edit mode toggle
  const handleToggleEditMode = (editing: boolean) => {
    trackEvent("support_event_edit_mode_toggled", {
      report_id: report.id,
      report_type: report.type,
      editing,
      event_title: report.eventDetails?.title,
    });
    setIsEditing(editing);
  };

  // Track cancel dialog open
  const handleOpenCancelDialog = () => {
    trackEvent("support_event_cancel_dialog_opened", {
      report_id: report.id,
      report_type: report.type,
      event_title: report.eventDetails?.title,
      participants_count: report.eventDetails?.participants?.length || 0,
    });
    setShowCancelDialog(true);
  };

  return (
    <>
      <Card
        data-report-id={report.id}
        className={`border-border overflow-hidden transition-all duration-200 hover:shadow-md ${isHighlighted ? "bg-primary/5 ring-primary ring-2 ring-offset-2" : ""}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-lg">
                {getTypeIcon(report.type)}
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base">
                    {getTypeLabel(report.type, t)}{" "}
                    {t("community.support.report_card.report_suffix")}
                  </CardTitle>
                  {getStatusBadge(report.status, report, t)}
                </div>
                <CardDescription>
                  {report.isOwnContent
                    ? t("community.support.report_card.your_content_reported")
                    : t("community.support.report_card.reported_on", {
                        date: formatDate(report.reportedAt),
                      })}
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Content Preview */}
          {(report.contentTitle || report.contentPreview) && !isEditing && (
            <div className="bg-muted/50 space-y-1 rounded-lg p-3">
              {report.contentTitle && <p className="text-sm font-medium">{report.contentTitle}</p>}
              {report.contentPreview && (
                <p className="text-muted-foreground line-clamp-2 text-sm">
                  "{report.contentPreview}"
                </p>
              )}
            </div>
          )}

          {/* Reason */}
          <Alert variant="default" className="border-amber-200 bg-amber-50">
            <AlertTriangle className="mt-1 h-4 w-4 text-amber-700" />
            <AlertTitle className="text-sm font-medium">
              {t("community.support.report_card.reason", {
                reason: formatReasonLabel(report.reason, t),
              })}
            </AlertTitle>
            {report.description && (
              <AlertDescription className="text-muted-foreground mt-1 text-sm">
                {report.description}
              </AlertDescription>
            )}
          </Alert>

          {/* Reported User (for reports made by current user) */}
          {!report.isOwnContent && report.reportedUser && (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <Users className="h-4 w-4" />
              <span>
                {t("community.support.report_card.reported_user")}{" "}
                <span className="text-foreground font-medium">{report.reportedUser.name}</span>
              </span>
            </div>
          )}

          {/* Resubmission Status Message - Show for resubmitted, approved, or rejected status */}
          {(report.eventMetadata?.status === "resubmitted" ||
            report.eventMetadata?.status === "approved" ||
            report.eventMetadata?.status === "rejected") && (
            <Alert
              className={
                report.eventMetadata?.status === "approved"
                  ? "border-[#a7f3d0] bg-[#ecfdf5]"
                  : report.eventMetadata?.status === "rejected"
                    ? "border-destructive/30 bg-destructive/10"
                    : "border-brand-accent-border bg-brand-accent"
              }
            >
              {report.eventMetadata?.status === "approved" && (
                <CheckCircle className="h-4 w-4 text-[#047857]" />
              )}
              {report.eventMetadata?.status === "rejected" && (
                <XCircle className="text-destructive h-4 w-4" />
              )}
              {report.eventMetadata?.status === "resubmitted" && (
                <RefreshCw className="text-brand-accent-foreground h-4 w-4 animate-spin" />
              )}
              <AlertTitle className="text-sm">
                {report.eventMetadata?.status === "approved" &&
                  t("community.support.resubmission_status.approved")}
                {report.eventMetadata?.status === "rejected" &&
                  t("community.support.resubmission_status.rejected")}
                {report.eventMetadata?.status === "resubmitted" &&
                  t("community.support.resubmission_status.pending_review")}
              </AlertTitle>
              {/* {report.admin_comment && (
                <AlertDescription>
                  {report.admin_comment}
                </AlertDescription>
              )} */}
            </Alert>
          )}

          {/* Event Details Preview - Show for event reports that are not in edit section, or for cancelled events */}
          {report.eventDetails &&
            (report.eventMetadata?.status === "event_cancelled" ||
              report.eventDetails?.status === "cancelled" ||
              (!showEditSection &&
                report.eventMetadata?.status !== "resubmitted" &&
                report.eventMetadata?.status !== "approved")) && (
              <Card
                className={
                  report.eventMetadata?.status === "event_cancelled" ||
                  report.eventDetails?.status === "cancelled"
                    ? "border-destructive/30 bg-destructive/10"
                    : "bg-background"
                }
              >
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Calendar className="text-primary h-5 w-5" />
                    {report.eventDetails.title}
                    {report?.eventDetails?.status === "cancelled" && (
                      <Badge variant="destructive" className="ml-2">
                        {t("community.support.event_edit.cancelled")}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="text-muted-foreground h-4 w-4" />
                      <span>
                        {report.eventDetails.date.toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                        {" at "}
                        {report.eventDetails.date.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {report.eventDetails.endDate && (
                          <>
                            {" - "}
                            {report.eventDetails.endDate.toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="text-muted-foreground h-4 w-4" />
                      <span>{report.eventDetails.location}</span>
                    </div>
                    {(report.eventDetails.maxParticipants ||
                      report.eventDetails.participantCount !== undefined) && (
                      <div className="flex items-center gap-2">
                        <Users className="text-muted-foreground h-4 w-4" />
                        <span>
                          {report.eventDetails.participantCount ??
                            report.eventDetails.participants?.length ??
                            0}
                          {report.eventDetails.maxParticipants &&
                            ` / ${report.eventDetails.maxParticipants}`}{" "}
                          {t("community.support.event_edit.participants")}
                        </span>
                      </div>
                    )}
                  </div>

                  {report.eventDetails.description && (
                    <>
                      <Separator />
                      <div
                        className="prose prose-sm text-muted-foreground max-w-none text-sm"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(report.eventDetails.description),
                        }}
                      />
                    </>
                  )}

                  {/* Participants Notice */}
                  {report.eventDetails.participantCount &&
                    report.eventDetails.participantCount > 0 && (
                      <>
                        <Separator />
                        <div className="text-muted-foreground flex items-center gap-2 text-sm">
                          <Bell className="h-4 w-4" />
                          <span>
                            {t("community.support.event_edit.participants_notified", {
                              count: report.eventDetails.participantCount,
                            })}
                          </span>
                        </div>
                      </>
                    )}
                </CardContent>
              </Card>
            )}

          {/* Event Edit Section - Special handling for events with "remove and warn" or rejected */}
          {showEditSection &&
            report.eventMetadata?.status !== "resubmitted" &&
            report.eventMetadata?.status !== "approved" &&
            report.eventMetadata?.status !== "event_cancelled" &&
            report.eventDetails?.status !== "cancelled" && (
              <Card
                className={
                  report.eventMetadata?.status === "rejected"
                    ? "border-destructive/30 bg-destructive/10"
                    : "border-amber-200 bg-amber-50"
                }
              >
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-destructive flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">
                        {timeRemaining?.expired
                          ? t("community.support.event_edit.deadline_passed")
                          : timeRemaining?.text || "0h 0m remaining"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          report.eventMetadata?.status === "rejected"
                            ? "border-destructive/30 bg-destructive/10 text-destructive"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                        }
                      >
                        {t("community.support.event_edit.event_hidden")}
                      </Badge>

                      {isActionRequiredStatus && (
                        <Badge
                          variant="outline"
                          className={
                            remainingAttempts <= 1
                              ? "border-destructive/30 bg-destructive/10 text-destructive"
                              : "border-brand-accent-border bg-brand-accent text-brand-accent-foreground"
                          }
                        >
                          {t("community.support.event_edit.submissions_remaining", {
                            count: remainingAttempts,
                          })}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {isActionRequiredStatus && (
                    <>
                      {/* Progress bar - only show for waiting_action with timeRemaining */}
                      {timeRemaining && (
                        <Progress value={timeRemaining.percentage} className="h-2" />
                      )}

                      <Alert
                        variant="destructive"
                        className="border-destructive/30 bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                        <AlertTitle>
                          {t("community.support.event_edit.auto_deletion_warning")}
                        </AlertTitle>
                        <AlertDescription className="text-destructive">
                          {t("community.support.event_edit.auto_deletion_message", {
                            count: report.eventDetails?.participants?.length || 0,
                          })}
                        </AlertDescription>
                      </Alert>

                      {/* Admin Note */}
                      {report.action?.adminNote && (
                        <div className="bg-background space-y-1 rounded-lg border p-4">
                          <p className="text-sm font-medium">
                            {t("community.support.report_card.admin_note")}
                          </p>
                          <p className="text-muted-foreground text-sm">{report.action.adminNote}</p>
                        </div>
                      )}

                      {!isEditing ? (
                        <>
                          {/* Event Preview Card */}
                          {report.eventDetails && (
                            <Card className="bg-background">
                              <CardHeader className="pb-2">
                                <CardTitle className="flex items-center gap-2 text-lg">
                                  <Calendar className="text-primary h-5 w-5" />
                                  {report.eventDetails.title}
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                <div className="grid gap-2 text-sm">
                                  <div className="flex items-center gap-2">
                                    <Clock className="text-muted-foreground h-4 w-4" />
                                    <span>
                                      {report.eventDetails.date.toLocaleDateString("en-US", {
                                        weekday: "long",
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                      })}
                                      {" at "}
                                      {report.eventDetails.date.toLocaleTimeString("en-US", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                      {report.eventDetails.endDate && (
                                        <>
                                          {" - "}
                                          {report.eventDetails.endDate.toLocaleTimeString("en-US", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </>
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <MapPin className="text-muted-foreground h-4 w-4" />
                                    <span>{report.eventDetails.location}</span>
                                  </div>
                                  {report.eventDetails.maxParticipants && (
                                    <div className="flex items-center gap-2">
                                      <Users className="text-muted-foreground h-4 w-4" />
                                      <span>
                                        {report.eventDetails.participants?.length || 0} /{" "}
                                        {report.eventDetails.maxParticipants}{" "}
                                        {t("community.support.event_edit.participants")}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {report.eventDetails.description && (
                                  <>
                                    <Separator />
                                    <div
                                      className="prose prose-sm text-muted-foreground max-w-none text-sm"
                                      dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(report.eventDetails.description),
                                      }}
                                    />
                                  </>
                                )}

                                {/* Participants Notice */}
                                {report.eventDetails.participants &&
                                  report.eventDetails.participants.length > 0 && (
                                    <>
                                      <Separator />
                                      <div className="text-muted-foreground flex items-center gap-2 text-sm">
                                        <Bell className="h-4 w-4" />
                                        <span>
                                          {t("community.support.event_edit.participants_notified", {
                                            count: report.eventDetails.participants.length,
                                          })}
                                        </span>
                                      </div>
                                    </>
                                  )}
                              </CardContent>
                            </Card>
                          )}

                          {/* Only show edit/cancel buttons if deadline hasn't expired */}
                          {(!timeRemaining || !timeRemaining.expired) && (
                            <div className="flex gap-3">
                              <Button
                                onClick={() => handleToggleEditMode(true)}
                                className="flex-1 gap-2"
                                disabled={remainingAttempts <= 0}
                              >
                                <Edit3 className="h-4 w-4" />
                                {t("community.support.event_edit.edit_resubmit")}
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={handleOpenCancelDialog}
                                className="gap-2"
                              >
                                <XCircle className="h-4 w-4" />
                                {t("community.support.event_edit.cancel_event")}
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        /* Event Edit Form */
                        <Card className="bg-background">
                          <CardHeader className="pb-4">
                            <CardTitle className="flex items-center gap-2 text-base">
                              <Edit3 className="h-4 w-4" />
                              {t("community.support.event_edit.edit_event_details")}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div className="flex flex-col gap-2">
                              <Label htmlFor="event-title">
                                {t("community.support.event_edit.event_title")}{" "}
                                <span className="text-destructive">*</span>
                              </Label>
                              <Input
                                id="event-title"
                                type="text"
                                value={editEventTitle}
                                onChange={(e) => handleEditEventTitleChange(e.target.value)}
                                placeholder={t(
                                  "community.support.event_edit.event_title_placeholder",
                                )}
                                className={editEventTitleError ? "border-destructive" : ""}
                              />
                              {editEventTitleError && (
                                <p className="text-destructive mt-1 text-xs">
                                  {editEventTitleError}
                                </p>
                              )}
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              <div className="flex flex-col gap-2">
                                <Label>
                                  {t("community.support.event_edit.date")}{" "}
                                  <span className="text-destructive">*</span>
                                </Label>
                                <ShadCNDatePicker
                                  value={editEventDate}
                                  onChange={setEditEventDate}
                                  placeholder={t("community.create_post.pick_date")}
                                  disablePastDates
                                />
                              </div>
                              <div className="flex flex-col gap-2">
                                <Label htmlFor="max-participants">
                                  {t("community.support.event_edit.max_participants")}
                                </Label>
                                <Input
                                  id="max-participants"
                                  type="number"
                                  value={editEventMaxParticipants}
                                  onChange={(e) => setEditEventMaxParticipants(e.target.value)}
                                  placeholder={t(
                                    "community.support.event_edit.max_participants_placeholder",
                                  )}
                                  min="1"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <TimeInput
                                id="edit-event-start-time"
                                label={t("community.support.event_edit.start_time")}
                                value={editEventTime}
                                onChange={handleEditEventTimeChange}
                                required
                              />
                              <div>
                                <TimeInput
                                  id="edit-event-end-time"
                                  label={t("community.support.event_edit.end_time")}
                                  value={editEventEndTime}
                                  onChange={handleEditEventEndTimeChange}
                                />
                                {editEventTimeError && (
                                  <p className="text-destructive mt-1 text-xs">
                                    {editEventTimeError}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2"></div>

                            <div className="flex flex-col gap-2">
                              <Label htmlFor="location">
                                {t("community.support.event_edit.location")}{" "}
                                <span className="text-destructive">*</span>
                              </Label>
                              <Input
                                id="location"
                                type="text"
                                value={editEventLocation}
                                onChange={(e) => setEditEventLocation(e.target.value)}
                                placeholder={t("community.support.event_edit.location_placeholder")}
                              />
                            </div>

                            <div className="flex flex-col gap-2">
                              <Label htmlFor="description">
                                {t("community.support.event_edit.description")}
                              </Label>
                              <Tiptap
                                ref={editTextareaRef}
                                content={editEventDescription}
                                onChange={setEditEventDescription}
                                placeholder={t(
                                  "community.support.event_edit.description_placeholder",
                                )}
                                enableLink={true}
                                enableAutolink={true}
                                enableEmoji={true}
                                enableMention={false}
                              />
                            </div>

                            <Separator />

                            <div className="flex gap-3">
                              <Button
                                onClick={handleResubmit}
                                className="flex-1 gap-2"
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                                {isSubmitting
                                  ? t("community.support.event_edit.submitting")
                                  : t("community.support.event_edit.resubmit_review")}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => handleToggleEditMode(false)}
                                disabled={isSubmitting}
                              >
                                {t("community.support.event_edit.cancel")}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}

                  {timeRemaining?.expired && (
                    <Alert variant="destructive">
                      <XCircle className="h-4 w-4" />
                      <AlertTitle>
                        {t("community.support.event_edit.deadline_expired_title")}
                      </AlertTitle>
                      <AlertDescription className="text-destructive">
                        {t("community.support.event_edit.deadline_expired_description")}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}

          {/* Resubmit Status for events - show pending_review only while submitting */}
          {(report.action?.resubmitStatus === "approved" ||
            report.action?.resubmitStatus === "rejected") && (
            <Alert
              className={
                report.action.resubmitStatus === "approved"
                  ? "border-[#a7f3d0] bg-[#ecfdf5]"
                  : "border-destructive/30 bg-destructive/10"
              }
            >
              {report.action.resubmitStatus === "approved" && (
                <CheckCircle className="h-4 w-4 text-[#047857]" />
              )}
              {report.action.resubmitStatus === "rejected" && (
                <XCircle className="text-destructive h-4 w-4" />
              )}
              <AlertTitle className="text-sm">
                {report.action.resubmitStatus === "approved" &&
                  t("community.support.event_edit.resubmit_approved_message")}
                {report.action.resubmitStatus === "rejected" &&
                  t("community.support.event_edit.resubmit_rejected_message")}
              </AlertTitle>
              {report.action.resubmitReviewNote && (
                <AlertDescription>{report.action.resubmitReviewNote}</AlertDescription>
              )}
            </Alert>
          )}

          {/* Show pending review message only while submitting */}
          {isSubmitting && (
            <Alert className="border-brand-accent-border bg-brand-accent">
              <RefreshCw className="text-brand-accent-foreground h-4 w-4 animate-spin" />
              <AlertTitle className="text-sm">
                {t("community.support.event_edit.resubmit_pending_message")}
              </AlertTitle>
            </Alert>
          )}

          {/* Action Details (expandable) */}
          {report?.action_taken && (
            <Collapsible open={expanded} onOpenChange={handleToggleExpand}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-full justify-start gap-2 text-sm"
                >
                  {expanded ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t("community.support.report_card.hide_action_details")}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t("community.support.report_card.view_action_details")}
                    </>
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3">
                <div className="bg-muted/50 space-y-3 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {t("community.support.report_card.action_label")}
                    </span>
                    {getActionBadge(report.action_taken, t)}
                  </div>
                  {report.reviewedAt && (
                    <div className="text-muted-foreground flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4" />
                      {t("community.support.report_card.reviewed_on", {
                        date: formatDate(report.reviewedAt),
                      })}
                    </div>
                  )}
                  {report.admin_comment && (
                    <>
                      <Separator />
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {t("community.support.report_card.admin_note")}
                        </p>
                        <p className="text-muted-foreground text-sm">{report.admin_comment}</p>
                      </div>
                    </>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Pending Status Message */}
          {report.status === "pending" && (
            <div className="text-muted-foreground flex items-center gap-2 text-sm italic">
              <Clock className="h-4 w-4" />
              {t("community.support.report_card.pending_message")}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Event Dialog */}
      <AlertDialog
        open={showCancelDialog}
        onOpenChange={(open) => {
          if (!isCancelling) {
            if (!open && showCancelDialog) {
              trackEvent("support_cancel_dialog_dismissed", {
                report_id: report.id,
                report_type: report.type,
                event_title: report.eventDetails?.title,
                had_reason: !!cancelReason.trim(),
              });
            }
            setShowCancelDialog(open);
            if (!open) setCancelReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("community.support.cancel_dialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("community.support.cancel_dialog.description", {
                title: report.eventDetails?.title,
                count: report.eventDetails?.participants?.length || 0,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="cancel-reason" className="text-sm font-medium">
              {t("community.support.cancel_dialog.reason_label")}
            </Label>
            <textarea
              id="cancel-reason"
              className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring mt-2 min-h-[80px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={t("community.support.cancel_dialog.reason_placeholder")}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              disabled={isCancelling}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>
              {t("community.support.cancel_dialog.keep_event")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelEvent}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isCancelling}
            >
              {isCancelling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("community.support.cancel_dialog.cancelling")}
                </>
              ) : (
                t("community.support.cancel_dialog.yes_cancel")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function SupportPage() {
  const { trackEvent } = usePosthog();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const reportIdFromUrl = searchParams.get("reportId");
  const tabFromUrl = searchParams.get("tab");
  const initialTab =
    tabFromUrl === "reports-on-my-content"
      ? "reports-on-my-content"
      : tabFromUrl === "my-reports"
        ? "my-reports"
        : reportIdFromUrl
          ? "reports-on-my-content"
          : "my-reports";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [reports, setReports] = useState<Report[]>([]);
  const [ownReportedContent, setOwnReportedContent] = useState<Report[]>([]);
  const [highlightedReportId, setHighlightedReportId] = useState<string | null>(reportIdFromUrl);

  // API states for My Reports
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [reportsStats, setReportsStats] = useState<ReportsStats | null>(null);
  const [reportsPagination, setReportsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // API states for Reports on My Content
  const [isLoadingOwnReports, setIsLoadingOwnReports] = useState(false);
  const [ownReportsError, setOwnReportsError] = useState<string | null>(null);
  const [ownReportsStats, setOwnReportsStats] = useState<ReportsStats | null>(null);
  const [ownReportsPagination, setOwnReportsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Track page view on mount
  useEffect(() => {
    trackEvent("support_page_viewed", {
      initial_tab: initialTab,
    });
  }, [trackEvent, initialTab]);

  // Sync activeTab with URL tab parameter
  useEffect(() => {
    if (tabFromUrl === "reports-on-my-content" || tabFromUrl === "my-reports") {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Scroll to specific report card when reportId is in URL and data is loaded
  useEffect(() => {
    if (reportIdFromUrl && ownReportedContent.length > 0 && !isLoadingOwnReports) {
      // Update highlighted state when URL changes (important when already on this page)
      setHighlightedReportId(reportIdFromUrl);
      // Ensure we're on the correct tab for reports on my content
      setActiveTab("reports-on-my-content");
      // Delay to ensure tab switch and DOM is updated
      const timer = setTimeout(() => {
        const reportElement = document.querySelector(`[data-report-id="${reportIdFromUrl}"]`);
        if (reportElement) {
          trackEvent("support_report_scrolled_to", {
            report_id: reportIdFromUrl,
            source: "url_navigation",
          });
          reportElement.scrollIntoView({ behavior: "smooth", block: "center" });
          // Clear the highlight after 3 seconds
          setTimeout(() => {
            setHighlightedReportId(null);
            // Remove reportId and tab from URL without navigation
            setSearchParams(
              (params) => {
                params.delete("reportId");
                params.delete("tab");
                return params;
              },
              { replace: true },
            );
          }, 3000);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [reportIdFromUrl, ownReportedContent, isLoadingOwnReports, setSearchParams]);

  // Fetch my reports from API
  const fetchMyReports = useCallback(
    async (page: number = 1, isRetry: boolean = false) => {
      setIsLoadingReports(true);
      setReportsError(null);
      try {
        const response = await getMyReports(page, reportsPagination.limit);
        const transformedReports = response.reports.map(transformApiReport);
        setReports(transformedReports);
        setReportsPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total,
          totalPages: response.pagination.total_pages,
        });
        trackEvent("support_my_reports_loaded", {
          page,
          total_reports: response.pagination.total,
          reports_count: response.reports.length,
          is_retry: isRetry,
        });
      } catch (error) {
        console.error("Failed to fetch my reports:", error);
        setReportsError("Failed to load reports. Please try again.");
        trackEvent("support_my_reports_load_failed", {
          page,
          error: error instanceof Error ? error.message : "Unknown error",
          is_retry: isRetry,
        });
      } finally {
        setIsLoadingReports(false);
      }
    },
    [reportsPagination.limit, trackEvent],
  );

  // Fetch stats for my reports from dedicated API
  const fetchMyReportsStatsData = useCallback(async () => {
    try {
      const stats = await getMyReportsStats();
      setReportsStats({
        posts: stats.by_content_type.posts,
        comments: stats.by_content_type.comments,
        replies: stats.by_content_type.replies,
        events: stats.by_content_type.events,
        total: stats.total,
      });
      trackEvent("support_my_reports_stats_loaded", {
        total: stats.total,
        posts: stats.by_content_type.posts,
        comments: stats.by_content_type.comments,
        replies: stats.by_content_type.replies,
        events: stats.by_content_type.events,
      });
    } catch (error) {
      console.error("Failed to fetch my reports stats:", error);
      trackEvent("support_my_reports_stats_load_failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }, [trackEvent]);

  // Transform API report for "on my content" - uses different API response structure
  const transformOwnContentReport = (apiReport: OwnContentReport): Report => {
    // Determine component status based on API status
    const getComponentStatus = (): ReportStatus => {
      if (apiReport.status === "pending") return "pending";
      // All other statuses (actioned, dismissed, resolved) are considered actioned
      return "actioned";
    };

    // Determine the actual type from target_type
    // For events, check if event_details exists to differentiate from regular posts
    const hasEventDetails = !!apiReport.event_details;
    const contentType = hasEventDetails ? "event" : apiReport.target_type;
    const typeMap: Record<string, ReportType> = {
      post: "post",
      event: "event",
      comment: "comment",
      reply: "reply",
    };

    // Map action to admin action type (API values match our type directly)
    const actionMap: Record<string, AdminAction> = {
      keep: "keep",
      remove_warning: "remove_warning",
      suspend: "suspend",
      ban: "ban",
      hold: "hold",
    };

    // Calculate edit deadline from flagged_at + time_remaining_hours
    let editDeadline: Date | undefined;
    if (apiReport.action_summary?.flagged_at && apiReport.action_summary?.time_remaining_hours) {
      const flaggedAt = new Date(apiReport.action_summary.flagged_at);
      editDeadline = new Date(
        flaggedAt.getTime() + apiReport.action_summary.time_remaining_hours * 60 * 60 * 1000,
      );
    } else if (apiReport.action_taken === "remove_warning" || apiReport.action_taken === "hold") {
      const actionTakenAt = apiReport.reviewed_at
        ? new Date(apiReport.reviewed_at)
        : new Date(apiReport.updated_at);
      editDeadline = new Date(actionTakenAt.getTime() + 24 * 60 * 60 * 1000);
    }

    // Build event details if it's an event
    let eventDetails: EventDetails | undefined;
    if (apiReport.event_details) {
      const ed = apiReport.event_details;
      eventDetails = {
        title: ed.title,
        date: new Date(ed.date),
        endDate: ed.end_date ? new Date(ed.end_date) : undefined,
        location: ed.location,
        maxParticipants: ed.max_participants,
        description: ed.description,
        participantCount: apiReport.event_participants?.total_registered,
        status: ed.status,
      };
    }

    // Strip HTML tags from content preview (including truncated/incomplete tags)
    const contentPreview = apiReport.content_preview
      ?.replace(/<[^>]*>?/g, "")
      .trim()
      .substring(0, 100);

    // Build event metadata for section filtering
    // Derive status from API fields if not explicitly provided
    const deriveEventMetadataStatus = (): EventMetadataStatus => {
      if (apiReport.event_details?.status === "cancelled") {
        return "event_cancelled";
      }

      // If status is explicitly provided in event_metadata, use it
      if (apiReport.event_metadata?.status) {
        return apiReport.event_metadata.status as EventMetadataStatus;
      }

      // Otherwise derive from report status and other fields
      // If report is pending and has been flagged, it's waiting for action
      if (apiReport.status === "pending" && apiReport.action_summary?.action === "flagged") {
        return "waiting_action";
      }

      // If resubmission_count > 0 and status is still pending, it's resubmitted
      if (
        apiReport.event_metadata?.resubmission_count &&
        apiReport.event_metadata.resubmission_count > 0 &&
        apiReport.status === "pending"
      ) {
        return "resubmitted";
      }

      // If admin applied hold/remove_warning and report is actioned, user still needs to act
      if (
        (apiReport.status === "actioned" || apiReport.status === "resolved") &&
        (apiReport.action_taken === "remove_warning" || apiReport.action_taken === "hold")
      ) {
        return "waiting_action";
      }

      // If action was taken and resolved, it's approved
      if (apiReport.status === "actioned" || apiReport.status === "resolved") {
        return "approved";
      }

      // Default to waiting_action for any other pending state
      return "waiting_action";
    };

    let eventMetadata: EventMetadata | undefined;
    // Always create eventMetadata for reports on own content to enable section filtering
    eventMetadata = {
      status: deriveEventMetadataStatus(),
      resubmission_count: apiReport.event_metadata?.resubmission_count,
      remaining_attempts: apiReport.event_metadata?.remaining_attempts,
      max_attempts: apiReport.event_metadata?.max_attempts,
      has_new_action: apiReport.event_metadata?.has_new_action,
    };

    return {
      id: apiReport.target_id,
      type: typeMap[contentType] || "post",
      status: getComponentStatus(),
      reason: apiReport.reason || "Reported",
      description: undefined,
      reportedAt: new Date(apiReport.created_at),
      contentId: apiReport.target_id,
      contentTitle: apiReport.content_title || eventDetails?.title,
      contentPreview,
      eventDetails,
      eventMetadata,
      isOwnContent: true,
      reportedBy: {
        userId: apiReport.reporter_id,
        name: apiReport.reporter_name,
      },
      action: apiReport.action_taken
        ? {
            type: actionMap[apiReport.action_taken] || "hold",
            takenAt: apiReport.reviewed_at
              ? new Date(apiReport.reviewed_at)
              : new Date(apiReport.updated_at),
            adminNote: apiReport.admin_comment || apiReport.action_summary?.message,
            editDeadline,
            isEventHidden: apiReport.status === "actioned",
          }
        : undefined,
      action_taken: apiReport.action_taken
        ? actionMap[apiReport.action_taken] || "hold"
        : undefined,
      admin_comment: apiReport.admin_comment || apiReport.action_summary?.message,
      reviewedAt: apiReport.reviewed_at ? new Date(apiReport.reviewed_at) : undefined,
      reviewed_at: apiReport.reviewed_at ? new Date(apiReport.reviewed_at) : undefined,
    };
  };

  // Fetch reports on my content from API
  const fetchReportsOnMyContent = useCallback(
    async (page: number = 1, isRetry: boolean = false, showLoading: boolean = true) => {
      if (showLoading) {
        setIsLoadingOwnReports(true);
      }
      setOwnReportsError(null);
      try {
        const response = await getReportsOnMyContent(page, ownReportsPagination.limit);
        const transformedReports = response.reports.map(transformOwnContentReport);
        setOwnReportedContent(transformedReports);

        setOwnReportsPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total,
          totalPages: response.pagination.pages,
        });
        trackEvent("support_own_content_reports_loaded", {
          page,
          total_reports: response.pagination.total,
          reports_count: response.reports.length,
          is_retry: isRetry,
        });
      } catch (error) {
        console.error("Failed to fetch reports on my content:", error);
        setOwnReportsError("Failed to load reports. Please try again.");
        trackEvent("support_own_content_reports_load_failed", {
          page,
          error: error instanceof Error ? error.message : "Unknown error",
          is_retry: isRetry,
        });
      } finally {
        if (showLoading) {
          setIsLoadingOwnReports(false);
        }
      }
    },
    [ownReportsPagination.limit, trackEvent],
  );

  // Fetch stats for reports on my content from dedicated API
  const fetchOwnContentStats = useCallback(async () => {
    try {
      const stats = await getReportsOnMyContentStats();
      setOwnReportsStats({
        posts: stats.by_content_type.posts,
        comments: stats.by_content_type.comments,
        replies: stats.by_content_type.replies,
        events: stats.by_content_type.events,
        total: stats.total,
      });
      trackEvent("support_own_content_stats_loaded", {
        total: stats.total,
        posts: stats.by_content_type.posts,
        comments: stats.by_content_type.comments,
        replies: stats.by_content_type.replies,
        events: stats.by_content_type.events,
      });
    } catch (error) {
      console.error("Failed to fetch own content reports stats:", error);
      trackEvent("support_own_content_stats_load_failed", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }, [trackEvent]);

  // Fetch reports on component mount
  useEffect(() => {
    fetchMyReports();
    fetchMyReportsStatsData();
    fetchReportsOnMyContent();
    fetchOwnContentStats();
  }, [fetchMyReports, fetchMyReportsStatsData, fetchReportsOnMyContent, fetchOwnContentStats]);

  // Listen for report-related notifications to refresh the list
  useEffect(() => {
    const handleReportNotification = () => {
      // Silently refresh reports on my content without showing loading
      fetchReportsOnMyContent(ownReportsPagination.page, false, false);
      fetchOwnContentStats();
      trackEvent("support_reports_refreshed_from_notification");
    };

    window.addEventListener("reportNotificationReceived", handleReportNotification);

    return () => {
      window.removeEventListener("reportNotificationReceived", handleReportNotification);
    };
  }, [fetchReportsOnMyContent, fetchOwnContentStats, ownReportsPagination.page, trackEvent]);

  const pendingReports = reports.filter((r) => r.status === "pending");
  const resolvedReports = reports.filter((r) => r.status !== "pending");

  // Filter "Reports on My Content" into three sections based on event_metadata.status
  // Action Required: waiting_action or rejected (but NOT if event is cancelled)
  const actionRequiredReports = ownReportedContent.filter(
    (r) =>
      (r.eventMetadata?.status === "waiting_action" || r.eventMetadata?.status === "rejected") &&
      r.eventDetails?.status !== "cancelled",
  );
  // Resubmitted: resubmitted, event_cancelled, OR cancelled events that were waiting_action/rejected
  const resubmittedReports = ownReportedContent.filter(
    (r) =>
      r.eventMetadata?.status === "resubmitted" ||
      r.eventMetadata?.status === "event_cancelled" ||
      ((r.eventMetadata?.status === "waiting_action" || r.eventMetadata?.status === "rejected") &&
        r.eventDetails?.status === "cancelled"),
  );
  // Action Taken: approved
  const actionTakenReports = ownReportedContent.filter(
    (r) => r.eventMetadata?.status === "approved",
  );

  // For badge count, use action required reports
  const pendingOwnReports = actionRequiredReports;

  // Handle tab change with tracking
  const handleTabChange = (newTab: string) => {
    trackEvent("support_tab_changed", {
      from_tab: activeTab,
      to_tab: newTab,
      my_reports_count: reports.length,
      own_content_reports_count: ownReportedContent.length,
      pending_reports: pendingReports.length,
      pending_own_reports: pendingOwnReports.length,
    });
    setActiveTab(newTab);
  };

  // Handle pagination for my reports
  const handleMyReportsPagination = (page: number) => {
    trackEvent("support_my_reports_page_changed", {
      from_page: reportsPagination.page,
      to_page: page,
      total_pages: reportsPagination.totalPages,
      direction: page > reportsPagination.page ? "next" : "previous",
    });
    fetchMyReports(page);
  };

  // Handle pagination for own content reports
  const handleOwnReportsPagination = (page: number) => {
    trackEvent("support_own_content_reports_page_changed", {
      from_page: ownReportsPagination.page,
      to_page: page,
      total_pages: ownReportsPagination.totalPages,
      direction: page > ownReportsPagination.page ? "next" : "previous",
    });
    fetchReportsOnMyContent(page);
  };

  // Handle retry for my reports
  const handleRetryMyReports = () => {
    trackEvent("support_my_reports_retry_clicked", {
      current_page: reportsPagination.page,
    });
    fetchMyReports(reportsPagination.page, true);
  };

  // Handle retry for own content reports
  const handleRetryOwnReports = () => {
    trackEvent("support_own_content_reports_retry_clicked", {
      current_page: ownReportsPagination.page,
    });
    fetchReportsOnMyContent(ownReportsPagination.page, true);
  };

  const handleResubmitEvent = async (reportId: string, eventDetails: EventDetails) => {
    try {
      // Prepare payload for API
      const payload: ResubmitEventPayload = {
        event_details: {
          title: eventDetails.title,
          date: eventDetails.date.toISOString(),
          end_date: eventDetails.endDate?.toISOString(),
          location: eventDetails.location,
          description: eventDetails.description,
          max_participants: eventDetails.maxParticipants,
        },
      };

      // Call API to resubmit event
      await resubmitEvent(reportId, payload);

      // Update local state on success
      setOwnReportedContent(
        ownReportedContent.map((report) => {
          if (report.id === reportId) {
            return {
              ...report,
              status: "actioned" as ReportStatus,
              eventDetails: eventDetails,
              action: {
                ...report.action!,
                resubmittedAt: new Date(),
                resubmitStatus: "pending_review" as const,
                isEventHidden: true,
              },
            };
          }
          return report;
        }),
      );

      trackEvent("support_event_resubmit_success", {
        report_id: reportId,
        event_title: eventDetails.title,
      });

      // Silently refresh the reports list without showing loading
      fetchReportsOnMyContent(ownReportsPagination.page, false, false);
    } catch (error) {
      console.error("Failed to resubmit event:", error);
      trackEvent("support_event_resubmit_failed", {
        report_id: reportId,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      // The toast error will be shown by the ReportCard component
      throw error; // Re-throw to let ReportCard handle the error
    }
  };

  const handleCancelEvent = async (reportId: string, reason: string) => {
    try {
      // Call API to cancel flagged event
      await cancelFlaggedEvent(reportId, { reason });

      // Remove the report from local state on success
      setOwnReportedContent(ownReportedContent.filter((report) => report.id !== reportId));

      trackEvent("support_event_cancel_success", {
        report_id: reportId,
        reason,
      });
    } catch (error) {
      console.error("Failed to cancel event:", error);
      trackEvent("support_event_cancel_failed", {
        report_id: reportId,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      throw error; // Re-throw to let ReportCard handle the error
    }
  };

  const getContentTypeCounts = (reports: Report[]) => {
    return {
      posts: reports.filter((r) => r.type === "post").length,
      comments: reports.filter((r) => r.type === "comment").length,
      replies: reports.filter((r) => r.type === "reply").length,
      events: reports.filter((r) => r.type === "event").length,
    };
  };

  // Use API stats if available, otherwise calculate from reports
  const myReportCounts = reportsStats || getContentTypeCounts(reports);
  const ownContentCounts = ownReportsStats || getContentTypeCounts(ownReportedContent);

  const StatCard = ({
    icon: Icon,
    label,
    count,
  }: {
    icon: typeof FileText;
    label: string;
    count: number;
  }) => (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-lg">
              <Icon className="text-muted-foreground h-5 w-5" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm font-medium">{label}</p>
              <p className="text-2xl font-bold">{count}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-xl">
              <Headphones className="text-primary h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold tracking-tight sm:text-xl">
                {t("community.support.title")}
              </CardTitle>
              <CardDescription className="text-base">
                {t("community.support.description")}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="border-border h-auto w-full [scrollbar-width:none] justify-start gap-1 overflow-x-auto rounded-none border-b bg-transparent p-0 [&::-webkit-scrollbar]:hidden">
          <TabsTrigger
            value="my-reports"
            className="text-muted-foreground hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-primary relative -mb-px shrink-0 gap-2 rounded-none border-b-2 border-transparent bg-transparent px-3 py-3 text-sm font-semibold shadow-none transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            <Flag className="h-4 w-4" />
            <span className="hidden sm:inline">{t("community.support.tab_my_reports")}</span>
            <span className="sm:hidden">{t("community.support.tab_my_reports_short")}</span>
            {pendingReports.length > 0 && (
              <Badge
                variant="secondary"
                className="bg-muted hover:bg-muted ml-1 h-5 min-w-5 px-1.5 text-slate-600"
              >
                {pendingReports.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="reports-on-my-content"
            className="text-muted-foreground hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-primary relative -mb-px shrink-0 gap-2 rounded-none border-b-2 border-transparent bg-transparent px-3 py-3 text-sm font-semibold shadow-none transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t("community.support.tab_reports_on_my_content")}
            </span>
            <span className="sm:hidden">{t("community.support.tab_my_content_short")}</span>
            {pendingOwnReports.length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-5 px-1.5">
                {pendingOwnReports.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-reports" className="mt-0 space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              icon={FileText}
              label={t("community.support.stats_posts")}
              count={myReportCounts.posts}
            />
            <StatCard
              icon={MessageSquare}
              label={t("community.support.stats_comments")}
              count={myReportCounts.comments}
            />
            <StatCard
              icon={MessageSquare}
              label={t("community.support.stats_replies")}
              count={myReportCounts.replies}
            />
            <StatCard
              icon={Calendar}
              label={t("community.support.stats_events")}
              count={myReportCounts.events}
            />
          </div>

          {/* Loading State */}
          {isLoadingReports && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Loader2 className="text-primary mb-4 h-8 w-8 animate-spin" />
                <p className="text-muted-foreground text-sm">
                  {t("community.support.loading_reports")}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Error State */}
          {reportsError && !isLoadingReports && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t("community.support.error_loading")}</AlertTitle>
              <AlertDescription className="flex items-center justify-between">
                <span>{t("community.support.error_loading_reports")}</span>
                <Button variant="outline" size="sm" onClick={handleRetryMyReports}>
                  {t("community.support.try_again")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {!isLoadingReports && !reportsError && reports.length > 0 ? (
            <div className="space-y-8">
              {/* Pending Reports Section */}
              {pendingReports.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                      <Clock className="h-4 w-4 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {t("community.support.pending_section_title")}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {t("community.support.pending_reports_count", {
                          count: pendingReports.length,
                        })}
                      </p>
                    </div>
                  </div>
                  <Separator />
                  <div className="space-y-4">
                    {pendingReports.map((report) => (
                      <ReportCard key={report.id} report={report} />
                    ))}
                  </div>
                </section>
              )}

              {/* Resolved Reports Section */}
              {resolvedReports.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#ecfdf5] text-[#047857]">
                      <CheckCircle className="h-4 w-4 text-[#047857]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {t("community.support.resolved_section_title")}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {t("community.support.resolved_reports_count", {
                          count: resolvedReports.length,
                        })}
                      </p>
                    </div>
                  </div>
                  <Separator />
                  <div className="space-y-4">
                    {resolvedReports.map((report) => (
                      <ReportCard key={report.id} report={report} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : !isLoadingReports && !reportsError ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <div className="bg-muted mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                  <Flag className="text-muted-foreground/50 h-8 w-8" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">
                  {t("community.support.no_reports_title")}
                </h3>
                <p className="text-muted-foreground max-w-sm text-sm">
                  {t("community.support.no_reports_description")}
                </p>
              </CardContent>
            </Card>
          ) : null}

          {/* Pagination */}
          {!isLoadingReports && !reportsError && reportsPagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleMyReportsPagination(reportsPagination.page - 1)}
                disabled={reportsPagination.page <= 1}
              >
                {t("community.support.previous")}
              </Button>
              <span className="text-muted-foreground text-sm">
                {t("community.support.page_of", {
                  current: reportsPagination.page,
                  total: reportsPagination.totalPages,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleMyReportsPagination(reportsPagination.page + 1)}
                disabled={reportsPagination.page >= reportsPagination.totalPages}
              >
                {t("community.support.next")}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="reports-on-my-content" className="mt-0 space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              icon={FileText}
              label={t("community.support.stats_posts")}
              count={ownContentCounts.posts}
            />
            <StatCard
              icon={MessageSquare}
              label={t("community.support.stats_comments")}
              count={ownContentCounts.comments}
            />
            <StatCard
              icon={MessageSquare}
              label={t("community.support.stats_replies")}
              count={ownContentCounts.replies}
            />
            <StatCard
              icon={Calendar}
              label={t("community.support.stats_events")}
              count={ownContentCounts.events}
            />
          </div>

          {/* Loading State */}
          {isLoadingOwnReports && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Loader2 className="text-primary mb-4 h-8 w-8 animate-spin" />
                <p className="text-muted-foreground text-sm">
                  {t("community.support.loading_own_content_reports")}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Error State */}
          {ownReportsError && !isLoadingOwnReports && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t("community.support.error_loading")}</AlertTitle>
              <AlertDescription className="flex items-center justify-between">
                <span>{t("community.support.error_loading_reports")}</span>
                <Button variant="outline" size="sm" onClick={handleRetryOwnReports}>
                  {t("community.support.try_again")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {!isLoadingOwnReports && !ownReportsError && ownReportedContent.length > 0 ? (
            <div className="space-y-8">
              {/* Info Alert */}
              <Alert>
                <Eye className="h-4 w-4" />
                <AlertTitle>{t("community.support.about_your_reports_title")}</AlertTitle>
                <AlertDescription>
                  {t("community.support.about_your_reports_description")}
                </AlertDescription>
              </Alert>

              {/* Action Required Section */}
              {actionRequiredReports.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                      <AlertTriangle className="h-4 w-4 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {t("community.support.action_required_title")}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {t("community.support.action_required_count", {
                          count: actionRequiredReports.length,
                        })}
                      </p>
                    </div>
                  </div>
                  <Separator />
                  <div className="space-y-4">
                    {actionRequiredReports.map((report) => (
                      <ReportCard
                        key={report.id}
                        report={report}
                        onResubmitEvent={handleResubmitEvent}
                        onCancelEvent={handleCancelEvent}
                        isHighlighted={highlightedReportId === report.id}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Resubmitted Section */}
              {resubmittedReports.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-brand-accent text-brand-accent-foreground flex h-10 w-10 items-center justify-center rounded-lg">
                      <RefreshCw className="text-brand-accent-foreground h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {t("community.support.resubmitted_title")}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {t("community.support.resubmitted_count", {
                          count: resubmittedReports.length,
                        })}
                      </p>
                    </div>
                  </div>
                  <Separator />
                  <div className="space-y-4">
                    {resubmittedReports.map((report) => (
                      <ReportCard
                        key={report.id}
                        report={report}
                        onResubmitEvent={handleResubmitEvent}
                        onCancelEvent={handleCancelEvent}
                        isHighlighted={highlightedReportId === report.id}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Action Taken Section */}
              {actionTakenReports.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#ecfdf5] text-[#047857]">
                      <CheckCircle className="h-4 w-4 text-[#047857]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {t("community.support.action_taken_title")}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {t("community.support.action_taken_count", {
                          count: actionTakenReports.length,
                        })}
                      </p>
                    </div>
                  </div>
                  <Separator />
                  <div className="space-y-4">
                    {actionTakenReports.map((report) => (
                      <ReportCard
                        key={report.id}
                        report={report}
                        onResubmitEvent={handleResubmitEvent}
                        onCancelEvent={handleCancelEvent}
                        isHighlighted={highlightedReportId === report.id}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : !isLoadingOwnReports && !ownReportsError ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#ecfdf5] text-[#047857]">
                  <CheckCircle className="h-8 w-8 text-[#047857]" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">
                  {t("community.support.all_clear_title")}
                </h3>
                <p className="text-muted-foreground max-w-sm text-sm">
                  {t("community.support.all_clear_description")}
                </p>
              </CardContent>
            </Card>
          ) : null}

          {/* Pagination */}
          {!isLoadingOwnReports && !ownReportsError && ownReportsPagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOwnReportsPagination(ownReportsPagination.page - 1)}
                disabled={ownReportsPagination.page <= 1}
              >
                {t("community.support.previous")}
              </Button>
              <span className="text-muted-foreground text-sm">
                {t("community.support.page_of", {
                  current: ownReportsPagination.page,
                  total: ownReportsPagination.totalPages,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOwnReportsPagination(ownReportsPagination.page + 1)}
                disabled={ownReportsPagination.page >= ownReportsPagination.totalPages}
              >
                {t("community.support.next")}
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Export function to calculate support notifications count
// This is used by Community to show notification badge on Support menu
// Note: Reports are now fetched from API, consider using a global state for notifications
export function getSupportNotificationCount(): number {
  // Reports are now loaded from API in the SupportPage component
  // Return 0 as a default - use API call getReportsOnMyContent() for actual count
  return 0;
}

// Export function to get reported event IDs with their status
// This is used by EventsPage to show reported events section
// Note: Reports are now fetched from API, consider using a global state for event reports
export function getReportedEventIds(): Array<{ eventId: string; status: ReportStatus }> {
  // Reports are now loaded from API in the SupportPage component
  // Return empty array as a default - use API call getReportsOnMyContent() for actual data
  return [];
}
